import type { B2BAssets, StatementBandSection } from '../types';

export interface B2BStatementBandProps {
    section: StatementBandSection;
    assets: B2BAssets;
}

/**
 * Statement band — Figma node `443:24097` (frame 1920 × 589).
 *
 * Absolute 1920-frame layout: a centred 920×590 image card with a TWK Ghost
 * editorial line overlaid at ≈44.6 % down. The text is decorative chrome from
 * the mock — there is no nav/wordmark in the live page.
 *
 * Geometry (% of the 1920 × 589 frame):
 *   card:        left 26.04 %, top 0 %, width 47.92 %, aspect 920/590
 *   statement:   centred in card, top 44.55 % of card (262.78 / 590),
 *                width 66.25 % of card (609.46 / 920)
 *   top scrim:   30.69 % of card height (181.06 / 590), black/40 → transparent
 *
 * Type sizing: 27.117px / 19.2 = 1.413vw (TWK Ghost, leading 0.94,
 * tracking −0.02em). Mobile fallback stacks photo above text at a relaxed
 * aspect ratio.
 */
export const B2BStatementBand = ({
    section,
    assets,
}: B2BStatementBandProps) => {
    const mediaSrc = section.mediaAssetKey
        ? assets[section.mediaAssetKey]
        : undefined;

    if (!mediaSrc) return null;

    return (
        <section className="w-full bg-white">
            {/* Desktop: 1920 × 589 absolute frame */}
            <div className="relative aspect-[1920/589] w-full max-md:hidden">
                <div className="absolute left-[26.04%] top-0 aspect-[920/590] w-[47.92%] overflow-hidden">
                    {section.isVideo ? (
                        <video
                            className="absolute inset-0 size-full object-cover"
                            src={mediaSrc}
                            autoPlay
                            muted
                            loop
                            playsInline
                        />
                    ) : (
                        <img
                            className="absolute inset-0 size-full object-cover"
                            src={mediaSrc}
                            alt=""
                        />
                    )}
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="absolute inset-x-0 top-0 h-[30.69%] bg-gradient-to-b from-black/40 to-transparent" />
                    <p className="absolute left-1/2 top-[40%] w-[90%] -translate-x-1/2 text-center font-twk-ghost text-[1.8vw] leading-[1.1] tracking-[-0.02em] text-[#f9f9f7] [text-wrap:balance]">
                        {section.text}
                    </p>
                </div>
            </div>

            {/* Mobile (Figma `521:26869`, 768×434 frame):
             *  - Photo card: 678 × 433.92 at (44.14, 0) → centred,
             *    88.28 % wide, aspect 678/434 = 1.562.
             *  - Top scrim: 30.7 % of card height (133.45 / 433.92).
             *  - Headline: (114.39, 193.68), 449.20 × 38, 27.117 px font.
             *    16.87 % left / 44.64 % top / 66.25 % wide of the card,
             *    3.531 vw font scaled to the 768 design frame. */}
            <div className="hidden w-full max-md:block">
                <div className="relative mx-auto aspect-[678/434] w-[88.28%] overflow-hidden">
                    {section.isVideo ? (
                        <video
                            className="absolute inset-0 size-full object-cover"
                            src={mediaSrc}
                            autoPlay
                            muted
                            loop
                            playsInline
                        />
                    ) : (
                        <img
                            className="absolute inset-0 size-full object-cover"
                            src={mediaSrc}
                            alt=""
                        />
                    )}
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="absolute inset-x-0 top-0 h-[30.76%] bg-gradient-to-b from-black/40 to-transparent" />
                    <p className="absolute left-[16.87%] top-[44.64%] w-[66.25%] text-center font-twk-ghost text-[3.531vw] leading-[0.94] tracking-[-0.02em] text-[#f9f9f7]">
                        {section.text}
                    </p>
                </div>
            </div>
        </section>
    );
};
